import {Component, ElementRef, Input, OnInit, OnChanges} from '@angular/core';
import {Subject} from 'rxjs';
import html2canvas from 'html2canvas';
import {AppContext} from "../../../app-context";
import {exportTableToExcel} from "../../../common/upload/excel.utils";
import {ConnectionType, DataType, TimeRange, TimeResolution} from "@flowmaps-com/flowmaps-typescriptmodels";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {SourceInfo} from "../../../utils/source-providers/sources-provider";
import {AppCommonUtils} from "../../../common/app-common-utils";
import {TranslateDirective} from "../../../common/utils/translate.directive";
import {downloadRawConnectionData} from "../../refdata/connections/connections-overview/connections-overview.component";
import {
    connectionCsvDataToJson,
    connectionsWithLinkedChargePoints
} from "../../refdata/connections/connections-overview/connection-data-export";
import {Handler} from "../../../common/handler";
import {View} from "../../../common/view";
import moment from "moment";
import {DataQueryFilters} from "../../measurements-component/measurements-handler.component";
import {EntityType} from "../../../handlers/entity";

@Component({
    selector: 'app-chart-download-button',
    templateUrl: './chart-download-button.component.html',
    styleUrls: ['./chart-download-button.component.css']
})
@Handler()
export class ChartDownloadButton extends View implements OnInit, OnChanges {
    appContext = AppContext;
    @Input() element: HTMLElement | ElementRef;
    @Input() tableElement: ElementRef;
    @Input() tableExportAction: () => void | Promise<void>;
    @Input() fileName: string;
    @Input() tableExportTitle: string;
    @Input() tableExportFileName: string;

    selectedConnections: SourceInfo[] = [];
    rawDataDownloadThreshold: number = 5;
    @Input() connectionTypes: ConnectionType[] | string[];
    @Input() optionalDataTypes: DataType[];
    timeRange: TimeRange;
    private selectedSources: SourceInfo[] = [];

    ngOnInit() {
        this.subscribeTo("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => {
            this.timeRange = f.timeRange;
            this.selectedSources = f.allSelectedSources || [];
            this.ngOnChanges();
        });
    }

    ngOnChanges() {
        this.selectedConnections = this.selectedSources
            .filter(s => s.type === EntityType.connection)
            .filter(s => (this.connectionTypes || []).includes(s.connectionType));
    }

    downloadImage() {
        const subject = new Subject<void>();
        const process = AppCommonUtils.waitForProcess(subject);
        process.subscribe();
        const element: HTMLElement = this.element ? this.element['nativeElement'] || this.element : document.body;
        const dropdownMenu = element.querySelector('.dropdown-menu') as HTMLElement;
        if (dropdownMenu) {
            dropdownMenu.style.display = 'none';
        }
        html2canvas(element).then(canvas => {
            try {
                let downloadLink = document.createElement('a');
                downloadLink.setAttribute('download', this.fileName || 'screenshot.png');
                canvas.toBlob(function (blob) {
                    let url = URL.createObjectURL(blob);
                    downloadLink.setAttribute('href', url);
                    downloadLink.click();
                });
            } finally {
                subject.next();
            }
        });
    }

    get rawDataExportSupported(): boolean {
        return (this.connectionTypes || []).filter(a => a).length > 0;
    }

    get quarterHourDataExportSupported(): boolean {
        return this.selectedConnections.some(connection => connection.connectionType === ConnectionType.Electricity);
    }

    get toManyConnectionsTooltip(): string {
        return `${TranslateDirective.getTranslation('Data download is only possible below a selection of')} ${this.rawDataDownloadThreshold + 1} ${TranslateDirective.getTranslation('connections').toLowerCase()}`;
    }

    get duration(): moment.Duration {
        return AppContext.durationForTimeRange(this.timeRange);
    }

    get tableExportSupported(): boolean {
        return !!this.tableElement || !!this.tableExportAction;
    }

    downloadTable = () => this.tableExportAction
        ? this.tableExportAction()
        : exportTableToExcel(this.tableElement, this.tableExportFileName || 'export.xlsx', this.tableExportTitle);

    belowRawDataThreshold = () => this.selectedConnections.length && this.selectedConnections.length <= this.rawDataDownloadThreshold;

    downloadRawData = (resolution: TimeResolution | string) =>  downloadRawConnectionData(this.timeRange, resolution as TimeResolution,
        connectionsWithLinkedChargePoints(this.selectedConnections
            .filter(c => resolution !== TimeResolution.minute && resolution !== 'minute'
                || c.connectionType === ConnectionType.Electricity)
            .map(c => c.source), (this.connectionTypes || []).includes(ConnectionType.ChargePoint)),
        this.optionalDataTypes, (csv: string) => connectionCsvDataToJson(csv, this.optionalDataTypes), RefdataUtils.connectionsComparator
    );
}
